/**
 * Per-topic formative quizzes.
 *
 * Each question has ONE correct answer plus distractors that mirror well-known
 * student misconceptions. The `feedback` field is shown when a student picks
 * that option - it explicitly names the misconception and corrects it, instead
 * of just saying "wrong".
 */
export interface QuizOption {
  text: string;
  correct?: boolean;
  /** Shown when the student picks this option. Names the misconception. */
  feedback: string;
}

export interface QuizQuestion {
  id: string;
  prompt: string;
  options: QuizOption[];
}

export const TOPIC_QUIZZES: Record<string, QuizQuestion[]> = {
  "lab-foundations": [
    {
      id: "sds",
      prompt:
        "You spill a small amount of dilute hydrochloric acid on your hand. What is the FIRST thing you should do?",
      options: [
        {
          text: "Wipe it off with a paper towel.",
          feedback:
            "Misconception: 'wipe first.' Wiping spreads the acid and leaves a thin film. Flushing with water dilutes it immediately.",
        },
        {
          text: "Neutralize it with sodium hydroxide.",
          feedback:
            "Misconception: 'neutralize on skin.' A strong base on skin causes an exothermic burn AND a chemical burn. Never neutralize on skin.",
        },
        {
          text: "Rinse with copious cool water for at least 15 minutes.",
          correct: true,
          feedback:
            "Correct. Flooding with water dilutes the acid below a hazardous concentration and removes it. Then notify the teacher.",
        },
      ],
    },
    {
      id: "sigfigs",
      prompt: "How many significant figures are in the measurement 0.00450 g?",
      options: [
        { text: "5", feedback: "Misconception: 'count every digit.' Leading zeros are not significant." },
        { text: "2", feedback: "Misconception: 'trailing zeros after a decimal don't count.' They DO - they signal precision." },
        { text: "3", correct: true, feedback: "Correct. Leading zeros are placeholders; the 4, 5, and trailing 0 are all significant." },
      ],
    },
  ],
  "periodic-table": [
    {
      id: "atomic-radius",
      prompt: "Which element has the LARGEST atomic radius: Li, Na, K, or Rb?",
      options: [
        { text: "Li", feedback: "Misconception: 'top of the group is biggest.' Atomic radius increases DOWN a group as new shells are added." },
        { text: "Rb", correct: true, feedback: "Correct. Down a group, each element adds an electron shell, so the atom gets larger." },
        { text: "All four are the same - they're all alkali metals.", feedback: "Misconception: 'same family = same size.' Family means similar chemistry, not identical properties." },
      ],
    },
    {
      id: "ionization",
      prompt: "Why does oxygen have a LOWER first ionization energy than nitrogen, even though oxygen is to the right?",
      options: [
        { text: "Oxygen's nucleus is weaker.", feedback: "Misconception: 'more protons = weaker pull.' Oxygen has MORE protons; the nucleus is stronger, not weaker." },
        { text: "Nitrogen has a half-filled p subshell, which is extra stable; removing an electron from oxygen breaks an electron pair, which is easier.", correct: true, feedback: "Correct. The N 2p³ half-filled configuration is unusually stable. Oxygen's paired 2p electron is easier to remove." },
        { text: "Oxygen is a gas and nitrogen is a metal.", feedback: "Both are diatomic nonmetal gases at room temperature. Phase isn't the cause." },
      ],
    },
  ],
  "atomic-structure": [
    {
      id: "orbitals",
      prompt: "What does the Bohr model get WRONG that the quantum-mechanical model corrects?",
      options: [
        { text: "It says electrons orbit in fixed circular paths like planets.", correct: true, feedback: "Correct. Electrons exist in orbitals - probability regions - not fixed orbits. Bohr is useful but oversimplified." },
        { text: "It uses energy levels, which are wrong.", feedback: "Misconception: 'quantization is wrong.' Energy levels ARE quantized - Bohr got that part right." },
        { text: "It says atoms have a nucleus.", feedback: "Both models agree that atoms have a dense nucleus." },
      ],
    },
  ],
  bonding: [
    {
      id: "imf-vs-bond",
      prompt: "When water boils, what is being broken?",
      options: [
        { text: "The covalent bonds inside H₂O molecules.", feedback: "Common misconception. Boiling does NOT break O-H covalent bonds (that would require ~460 kJ/mol). It breaks intermolecular forces." },
        { text: "The hydrogen bonds BETWEEN water molecules.", correct: true, feedback: "Correct. Boiling overcomes the IMFs (hydrogen bonds) holding molecules together. The molecules themselves stay intact." },
        { text: "The atoms split into protons and electrons.", feedback: "That would be ionization, not boiling. Phase changes don't break atoms." },
      ],
    },
  ],
  stoichiometry: [
    {
      id: "limiting",
      prompt: "If you have 4 mol H₂ and 1 mol O₂, which is the limiting reactant in 2H₂ + O₂ → 2H₂O?",
      options: [
        { text: "H₂, because there's more of it.", feedback: "Misconception: 'most moles = limiting.' Limiting is decided by the RATIO needed, not the raw amount." },
        { text: "O₂, because the equation needs 2 mol H₂ per 1 mol O₂; 1 mol O₂ would only need 2 mol H₂, so H₂ is in excess.", correct: true, feedback: "Correct. O₂ runs out first; 2 mol H₂ remain unreacted." },
        { text: "Neither - they're both fully consumed.", feedback: "Only true if the ratio matches the equation exactly. Here it doesn't." },
      ],
    },
    {
      id: "yield",
      prompt: "A reaction has a theoretical yield of 10.0 g and you collect 8.5 g. What is the percent yield?",
      options: [
        { text: "117.6%", feedback: "Misconception: 'flip the fraction.' Percent yield is actual ÷ theoretical, not the reverse." },
        { text: "85.0%", correct: true, feedback: "Correct. (8.5 / 10.0) × 100 = 85.0%." },
        { text: "1.5%", feedback: "That's the percent LOST, not the percent yield." },
      ],
    },
  ],
  "gas-laws": [
    {
      id: "boyle",
      prompt: "If you double the pressure on a gas at constant temperature, what happens to its volume?",
      options: [
        { text: "The volume doubles.", feedback: "Misconception: 'P and V change together.' Boyle's Law says P and V are INVERSELY related at constant T." },
        { text: "The volume is halved.", correct: true, feedback: "Correct. P₁V₁ = P₂V₂. Doubling P halves V." },
        { text: "The volume stays the same; only temperature affects volume.", feedback: "Pressure absolutely affects gas volume - that's the core of Boyle's Law." },
      ],
    },
    {
      id: "kelvin",
      prompt: "Why must temperature be in Kelvin for gas-law calculations?",
      options: [
        { text: "Kelvin is the SI unit, so it's just a rule.", feedback: "Partly true but not the reason. The real reason is mathematical." },
        { text: "Celsius can be negative, which would give negative or zero volumes - physically impossible.", correct: true, feedback: "Correct. Kelvin starts at absolute zero, so V/T ratios stay physically meaningful." },
        { text: "Kelvin and Celsius give the same answer anyway.", feedback: "They don't - a 10 °C difference is the same SIZE as 10 K, but the absolute values are very different." },
      ],
    },
  ],
  solutions: [
    {
      id: "saturated",
      prompt: "What does 'saturated solution' mean?",
      options: [
        { text: "The solution is full and no more solute will EVER dissolve.", feedback: "Misconception: 'saturated = permanent.' Heating the solvent often dissolves more." },
        { text: "The maximum solute has dissolved at the current temperature; any more sits undissolved.", correct: true, feedback: "Correct. Saturation is temperature-dependent and represents dynamic equilibrium." },
        { text: "The solution is colored.", feedback: "Color and saturation are unrelated. Saturated NaCl is colorless." },
      ],
    },
  ],
  "acid-base": [
    {
      id: "ph-scale",
      prompt: "A solution with pH = 3 is how many times more acidic than one with pH = 5?",
      options: [
        { text: "2 times", feedback: "Misconception: 'pH is linear.' pH is LOGARITHMIC - each unit is a factor of 10." },
        { text: "100 times", correct: true, feedback: "Correct. Each pH unit = 10× change in [H⁺]. pH 3 vs pH 5 = 10² = 100×." },
        { text: "10 times", feedback: "Close - that's only 1 pH unit. The difference here is 2 units." },
      ],
    },
    {
      id: "neutral",
      prompt: "A neutralization reaction always produces a solution with pH = 7. True or false?",
      options: [
        { text: "True - that's the definition of neutralization.", feedback: "Misconception. Neutralization produces a salt + water, but the salt can be acidic or basic. Only strong-acid + strong-base gives pH 7." },
        { text: "False - the resulting pH depends on whether the acid and base were strong or weak.", correct: true, feedback: "Correct. NH₄Cl (from NH₃ + HCl) is acidic; NaC₂H₃O₂ (from NaOH + acetic acid) is basic." },
      ],
    },
  ],
  thermochemistry: [
    {
      id: "exo-endo",
      prompt: "An ice pack feels cold because…",
      options: [
        { text: "It releases 'cold' into your skin.", feedback: "Misconception: 'cold is a thing.' Cold isn't transferred - heat is. The pack absorbs heat FROM your skin." },
        { text: "The dissolution inside is endothermic - it absorbs heat from your skin.", correct: true, feedback: "Correct. NH₄NO₃ dissolving is endothermic; it pulls heat from the surroundings (your hand)." },
        { text: "The chemicals inside are physically frozen.", feedback: "They're at room temperature before activation. Cold comes from the endothermic process." },
      ],
    },
  ],
  nuclear: [
    {
      id: "halflife",
      prompt: "After 3 half-lives, what fraction of the original radioactive sample remains?",
      options: [
        { text: "1/3", feedback: "Misconception: 'half-lives subtract.' Each half-life HALVES what's left, multiplicatively." },
        { text: "1/8", correct: true, feedback: "Correct. ½ × ½ × ½ = 1/8." },
        { text: "0 - it's all decayed.", feedback: "Half-life never reaches zero in finite time; it asymptotically approaches it." },
      ],
    },
    {
      id: "fission-fusion",
      prompt: "What's the key difference between fission and fusion?",
      options: [
        { text: "Fission joins nuclei; fusion splits them.", feedback: "Backwards. Fission SPLITS heavy nuclei; fusion JOINS light ones." },
        { text: "Fission splits heavy nuclei (e.g., U-235); fusion combines light nuclei (e.g., H isotopes).", correct: true, feedback: "Correct. Fission powers reactors today; fusion powers stars and is the goal of future reactors." },
        { text: "They're the same process under different names.", feedback: "They're opposite processes with very different energy profiles." },
      ],
    },
  ],
};